import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import i18n from '~/i18n'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import StickyNavBottom from '~/Common-Objects/Navigation/StickNavBottom'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import axios from '~/data/http/axios'
import EmailValidator from '~/Classes/emailValidator'
import helpers from '~/Classes/helpers'
import DBConnection from '~/Classes/siteVars'
import { persistAiGeneratedMarkup } from '~/utils/persistAiGeneratedMarkup'
import * as siteActions from '~/data/actions/siteActions'

function measureFrameHeight(iframe) {
    try {
        const doc = iframe?.contentDocument
        if (!doc) return 0
        const body = doc.body
        const html = doc.documentElement
        if (!body) return 0
        iframe.style.height = 'auto'
        html.style.height = 'auto'
        html.style.minHeight = '0'
        html.style.overflow = 'hidden'
        body.style.height = 'auto'
        body.style.minHeight = '0'
        body.style.overflow = 'hidden'
        let height = Math.max(body.scrollHeight || 0, html.scrollHeight || 0, 480)
        Array.from(doc.querySelectorAll('table, img')).forEach((el) => {
            height = Math.max(height, el.scrollHeight || 0, el.offsetHeight || 0)
        })
        return height
    } catch {
        return 0
    }
}

export default function LightPreview({
    kind,
    templateId,
    landingPageId,
    previewHtml,
    title,
    subtitle,
    inlineActions = false,
    campaign,
    changeStep,
    onAdvancedEditor,
    onReviewDetails,
    onSendNow,
    onTryAnother
}) {
    const dispatch = useDispatch()
    const user = useSelector((state) => state.user)
    const accountMaster = useSelector((state) => state.accountMaster)
    const approved = Boolean(accountMaster?.accountMaster?.approved)
    const defaultEmail = user?.userData?.emailAddress || ''
    const iframeRef = useRef(null)

    const [html, setHtml] = useState(previewHtml || '')
    const [frameHeight, setFrameHeight] = useState(640)
    const [iframeSrc, setIframeSrc] = useState('')
    const [loadingHtml, setLoadingHtml] = useState(
        (kind === 'email' && Boolean(templateId)) || (kind === 'landing' && Boolean(landingPageId))
    )
    const [testEmail, setTestEmail] = useState(defaultEmail)
    const [sendingPreview, setSendingPreview] = useState(false)
    const [emailError, setEmailError] = useState('')

    const showCampaignContinue = kind === 'email' && typeof changeStep === 'function'
    const showSendTest = kind === 'email' && templateId

    useEffect(() => {
        setHtml(previewHtml || '')
    }, [previewHtml])

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            if (kind === 'email' && templateId) {
                setLoadingHtml(true)
                try {
                    await persistAiGeneratedMarkup({ kind: 'email', templateId })
                    if (cancelled) return
                    const key = await helpers.createKey(0, 0, 0, campaign?.id || 0)
                    if (cancelled) return
                    const baseUrl = DBConnection.getConnString()
                    setIframeSrc(
                        baseUrl +
                            `/templatePreview?key=${key}&templateId=${templateId}&showClicks=false&type=&bots=&html=true&_=${Date.now()}`
                    )
                } catch (err) {
                    console.log(err)
                } finally {
                    if (!cancelled) setLoadingHtml(false)
                }
                return
            }

            if (kind === 'landing' && landingPageId) {
                setLoadingHtml(true)
                try {
                    const persisted = await persistAiGeneratedMarkup({ kind: 'landing', landingPageId })
                    if (cancelled) return
                    if (persisted?.url) {
                        const separator = String(persisted.url).includes('?') ? '&' : '?'
                        setIframeSrc(`${persisted.url}${separator}preview=true`)
                    }
                } catch (err) {
                    console.log(err)
                } finally {
                    if (!cancelled) setLoadingHtml(false)
                }
                return
            }

            setIframeSrc('')
            setLoadingHtml(false)
        }
        load()
        return () => {
            cancelled = true
        }
    }, [kind, templateId, landingPageId, campaign?.id])

    const sizePreview = useCallback(() => {
        const iframe = iframeRef.current
        const next = measureFrameHeight(iframe)
        if (next) setFrameHeight(next)
    }, [])

    useEffect(() => {
        const iframe = iframeRef.current
        const doc = iframe?.contentDocument
        if (!doc?.body || typeof ResizeObserver === 'undefined') return undefined
        const observer = new ResizeObserver(() => sizePreview())
        observer.observe(doc.body)
        Array.from(doc.images || []).forEach((img) => {
            img.addEventListener('load', sizePreview)
        })
        return () => observer.disconnect()
    }, [html, sizePreview])

    const sendTest = async () => {
        const trimmed = (testEmail || '').trim()
        if (EmailValidator.validateEmail(trimmed) !== true) {
            setEmailError(i18n.t('templates:preview.invalid'))
            return
        }
        setEmailError('')
        setSendingPreview(true)
        try {
            await axios.post(`/template/preview/${templateId}`, {
                emailAddress: [trimmed],
                subject: campaign?.subject,
                fromAddress: campaign?.fromAddress,
                fromName: campaign?.fromName,
                CdnDomainId: campaign?.CdnDomainId,
                TrackingDomainId: campaign?.TrackingDomainId
            })
            dispatch(siteActions.alterSnackbar(true, i18n.t('templates:preview.sent')))
        } catch (error) {
            dispatch(
                siteActions.alterSnackbar(
                    true,
                    error?.data?.error || error?.response?.data?.error || i18n.t('templates:preview.error')
                )
            )
        } finally {
            setSendingPreview(false)
        }
    }

    const canvasClass =
        kind === 'landing'
            ? 'ai-create-wizard__review-canvas ai-create-wizard__review-canvas--landing'
            : 'ai-create-wizard__review-canvas'

    const reviewActions = (
        <>
            {onTryAnother ? (
                <Button onClick={onTryAnother}>{i18n.t('templates:add.magicTools.tryAnother')}</Button>
            ) : null}
            <div className='ai-create-wizard__review-actions'>
                {showCampaignContinue ? (
                    <Button onClick={onAdvancedEditor} data-test='ai-create-advanced-editor'>
                        {i18n.t('templates:add.magicTools.advancedEditor')}
                    </Button>
                ) : null}
                {showCampaignContinue ? (
                    <Button
                        className='button-primary'
                        iconRight={<Icons icon='arrowRight' />}
                        onClick={onSendNow || onReviewDetails || (() => changeStep(3, true))}
                        data-test='ai-create-continue'
                    >
                        {i18n.t('templates:add.magicTools.continue')}
                    </Button>
                ) : (
                    <Button
                        className='button-primary'
                        iconRight={<Icons icon='arrowRight' />}
                        onClick={onAdvancedEditor}
                        data-test='ai-create-advanced-editor'
                    >
                        {i18n.t('templates:add.magicTools.editInEditor')}
                    </Button>
                )}
            </div>
        </>
    )

    return (
        <div className='ai-create-wizard__review' data-test='ai-create-preview'>
            {title ? (
                <div className='ai-create-wizard__header'>
                    <h2 className='ai-create-wizard__title'>{title}</h2>
                    {subtitle ? <p className='ai-create-wizard__subtitle'>{subtitle}</p> : null}
                </div>
            ) : null}
            {showSendTest ? (
                <div className='ai-create-wizard__review-top'>
                    <div className='ai-create-wizard__review-send-test'>
                        <TextInput
                            name='previewTestEmail'
                            placeholder={i18n.t('templates:add.magicTools.sendTestPlaceholder')}
                            value={testEmail}
                            onChange={(ev) => setTestEmail(ev.target.value)}
                            error={emailError}
                            readOnly={!approved}
                        />
                        <Button
                            className='button-primary'
                            text={i18n.t('templates:preview.send')}
                            disabled={!approved || sendingPreview || !testEmail}
                            onClick={sendTest}
                            data-test='ai-create-send-test'
                        />
                    </div>
                </div>
            ) : null}

            {loadingHtml ? (
                <LoadingIcons iconType='2' />
            ) : iframeSrc ? (
                <div className='emailPreviewHolder' style={{ width: '100%' }}>
                    <div className='innerPreviewHolder' style={{ width: '100%', maxWidth: '100%' }}>
                        <iframe
                            width='100%'
                            height='100%'
                            style={{ border: '0', width: '100%' }}
                            src={iframeSrc}
                            title={landingPageId ? `landing-preview-${landingPageId}` : 'email-preview'}
                        />
                    </div>
                </div>
            ) : (
                <div className='ai-create-wizard__review-stage'>
                    <div className={canvasClass}>
                        <iframe
                            ref={iframeRef}
                            title={landingPageId ? `landing-preview-${landingPageId}` : 'email-preview'}
                            srcDoc={html || ''}
                            sandbox='allow-same-origin allow-popups allow-forms'
                            scrolling='no'
                            onLoad={sizePreview}
                            style={{ height: `${frameHeight}px` }}
                        />
                    </div>
                </div>
            )}

            {inlineActions ? (
                <div className='ai-create-wizard__review-sticky mar-t20'>{reviewActions}</div>
            ) : (
                <StickyNavBottom>
                    <div className='ai-create-wizard__review-sticky'>{reviewActions}</div>
                </StickyNavBottom>
            )}
        </div>
    )
}
